import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { message } from "antd";
import { Button, Flex, Space, Tooltip } from "antd";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
// Components
import { MapComponent } from "@/components/MapComponent";
// Hooks
import { getRootStore } from "@/stores/RootStore";
// Types
import { MAP_COLORS } from "@/types/map";
// Utils
import { supabase } from "@/utils/supabase";
/**
 * RouteTracking Component
 *
 * Displays planned and/or actual route on a map with toggle controls.
 * Planned route: built from route_points via Yandex Router API.
 * Actual route: GPS track from technician_locations, drawn as-is.
 */
const RouteTracking = observer(({ routeData, routePoints, width = "100%", height = "500px", showLoadingSpinner = true, showErrorAlert = true, showCard = false, onRouteLoaded, onUnmount, }) => {
    const { mapStore } = getRootStore();
    const [showPlanned, setShowPlanned] = useState(true);
    const [showActual, setShowActual] = useState(false);
    const [isLoadingActual, setIsLoadingActual] = useState(false);
    const [actualPoints, setActualPoints] = useState([]);
    const [actualLoaded, setActualLoaded] = useState(false);
    const hasShownNotificationRef = useRef(false);
    // Track whether the initial map load has run for this routeData
    const initializedForRouteRef = useRef(null);
    // Ref to always hold the latest value of showPlanned (avoids stale closures in async init)
    const showPlannedRef = useRef(showPlanned);
    const plannedRouteId = `route-planned-${routeData.id}`;
    const actualRouteId = `route-actual-${routeData.id}`;
    // Build planned MapRoute from route_points
    const buildPlannedMapRoute = () => {
        const routePointsSafe = routePoints || [];
        const mapPoints = routePointsSafe
            .filter((point) => point.addressInfo.getLatitude() && point.addressInfo.getLongitude())
            .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
            .map((point) => ({
            address: point.addressInfo.getAddress() || "Адрес не указан",
            latitude: point.addressInfo.getLatitude() || 0,
            longitude: point.addressInfo.getLongitude() || 0,
            orderIndex: point.orderIndex || 0,
        }));
        return {
            id: plannedRouteId,
            points: mapPoints,
            color: MAP_COLORS.PRIMARY_ROUTE,
            weight: 4,
            title: `Плановый маршрут`,
            description: `${mapPoints.length} точек`,
            isVisible: true,
        };
    };
    // Build actual MapRoute from GPS points
    const buildActualMapRoute = (points) => {
        const mapPoints = points.map((loc, index) => ({
            latitude: Number(loc.latitude),
            longitude: Number(loc.longitude),
            address: `GPS точка ${index + 1}`,
            orderIndex: index,
        }));
        return {
            id: actualRouteId,
            points: mapPoints,
            color: MAP_COLORS.ACTUAL_ROUTE,
            weight: 3,
            title: "Фактический маршрут",
            description: `${mapPoints.length} GPS-точек`,
            isVisible: true,
            skipRouteBuilding: true,
        };
    };
    // Load actual GPS track from technician_locations
    const loadActualRoute = async () => {
        const routePointIds = (routePoints || []).map((p) => p.id).filter(Boolean);
        if (routePointIds.length === 0)
            return [];
        setIsLoadingActual(true);
        try {
            const { data, error } = await supabase()
                .from("technician_locations")
                .select("latitude, longitude")
                .in("route_point_to_id", routePointIds)
                .order("created_at", { ascending: true });
            if (error) {
                console.error("Error loading actual route:", error);
                if (showErrorAlert) {
                    message.error("Ошибка загрузки фактического маршрута");
                }
                return [];
            }
            return data || [];
        }
        catch (err) {
            console.error("Error loading actual route:", err);
            return [];
        }
        finally {
            setIsLoadingActual(false);
        }
    };
    // Keep showPlannedRef in sync with the latest state on every render
    showPlannedRef.current = showPlanned;
    // Initial map setup: clear and add planned route
    useEffect(() => {
        if (!mapStore.isInitialized)
            return;
        if (initializedForRouteRef.current === routeData.id)
            return;
        initializedForRouteRef.current = routeData.id;
        hasShownNotificationRef.current = false;
        const init = async () => {
            await Promise.all([
                mapStore.clearAllLocators(),
                mapStore.clearAllRoutes(),
            ]);
            // Re-check showPlannedRef AFTER async clear to handle toggle during load
            if (showPlannedRef.current) {
                const route = buildPlannedMapRoute();
                try {
                    await mapStore.addRoute(route);
                    // Race condition guard: user may have toggled OFF while addRoute was async
                    if (!showPlannedRef.current) {
                        await mapStore.removeRoute(route.id);
                        return;
                    }
                    if (route.points.length > 0) {
                        setTimeout(() => mapStore.fitBounds(route.points), 200);
                    }
                    if (showErrorAlert && !hasShownNotificationRef.current) {
                        message.success(`Маршрут отображен на карте: ${route.points.length} точек`);
                        hasShownNotificationRef.current = true;
                    }
                    onRouteLoaded?.(route);
                }
                catch (err) {
                    console.error("Error adding planned route:", err);
                    if (showErrorAlert)
                        message.error("Ошибка отображения маршрута на карте");
                }
            }
        };
        init();
    }, [mapStore.isInitialized, routeData.id, routePoints]);
    // Sync planned route when toggle changes (after initial load)
    useEffect(() => {
        if (!mapStore.isInitialized)
            return;
        if (initializedForRouteRef.current !== routeData.id)
            return;
        if (showPlanned) {
            if (!mapStore.getRouteById(plannedRouteId)) {
                const route = buildPlannedMapRoute();
                mapStore.addRoute(route).then(() => {
                    if (showPlannedRef.current && route.points.length > 0) {
                        setTimeout(() => mapStore.fitBounds(route.points), 200);
                    }
                });
            }
        }
        else {
            // Always try to remove — handles both:
            // 1. Route already in store (normal case)
            // 2. addRoute completed after toggle (race condition case)
            mapStore.removeRoute(plannedRouteId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fire only on toggle/init state, not on mapStore method identity
    }, [showPlanned, mapStore.isInitialized]);
    // Sync actual route when toggle or data changes
    useEffect(() => {
        if (!mapStore.isInitialized)
            return;
        if (showActual && actualPoints.length > 0) {
            if (!mapStore.getRouteById(actualRouteId)) {
                mapStore.addRoute(buildActualMapRoute(actualPoints));
            }
        }
        else {
            if (mapStore.getRouteById(actualRouteId)) {
                mapStore.removeRoute(actualRouteId);
            }
        }
    }, [showActual, actualPoints, mapStore.isInitialized]);
    // Toggle actual route: load data on first enable
    const handleToggleActual = async () => {
        const next = !showActual;
        setShowActual(next);
        if (next && !actualLoaded) {
            const points = await loadActualRoute();
            setActualPoints(points);
            setActualLoaded(true);
            if (points.length === 0 && showErrorAlert) {
                message.info("Фактический маршрут: GPS-данных пока нет");
            }
        }
    };
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            mapStore.clearAllLocators();
            mapStore.clearAllRoutes();
            onUnmount?.();
        };
    }, [mapStore, onUnmount]);
    return (_jsxs(Flex, { vertical: true, gap: 8, children: [_jsxs(Space, { size: 8, children: [_jsx(Tooltip, { title: "\u041F\u043B\u0430\u043D\u043E\u0432\u044B\u0439 \u043C\u0430\u0440\u0448\u0440\u0443\u0442 \u043F\u043E \u0442\u043E\u0447\u043A\u0430\u043C \u043C\u0430\u0440\u0448\u0440\u0443\u0442\u0430", children: _jsxs(Button, { size: "small", type: showPlanned ? "primary" : "default", onClick: () => setShowPlanned((v) => !v), style: {
                                borderColor: showPlanned ? MAP_COLORS.PRIMARY_ROUTE : undefined,
                                backgroundColor: showPlanned ? MAP_COLORS.PRIMARY_ROUTE : undefined,
                            }, children: [_jsx("span", { style: {
                                        display: "inline-block",
                                        width: 10,
                                        height: 10,
                                        borderRadius: "50%",
                                        backgroundColor: showPlanned ? "#fff" : MAP_COLORS.PRIMARY_ROUTE,
                                        marginRight: 6,
                                        verticalAlign: "middle",
                                    } }), "\u041F\u043B\u0430\u043D\u043E\u0432\u044B\u0439 \u043C\u0430\u0440\u0448\u0440\u0443\u0442"] }) }), _jsx(Tooltip, { title: actualLoaded && actualPoints.length === 0
                            ? "GPS-данных пока нет"
                            : "Фактический трек по GPS-координатам техника", children: _jsxs(Button, { size: "small", type: showActual ? "primary" : "default", loading: isLoadingActual, onClick: handleToggleActual, style: showActual
                                ? {
                                    borderColor: MAP_COLORS.ACTUAL_ROUTE,
                                    backgroundColor: MAP_COLORS.ACTUAL_ROUTE,
                                }
                                : undefined, children: [_jsx("span", { style: {
                                        display: "inline-block",
                                        width: 10,
                                        height: 10,
                                        borderRadius: "50%",
                                        backgroundColor: showActual ? "#fff" : MAP_COLORS.ACTUAL_ROUTE,
                                        marginRight: 6,
                                        verticalAlign: "middle",
                                    } }), "\u0424\u0430\u043A\u0442\u0438\u0447\u0435\u0441\u043A\u0438\u0439 \u043C\u0430\u0440\u0448\u0440\u0443\u0442", actualLoaded && actualPoints.length > 0 && (_jsxs("span", { style: { marginLeft: 4, opacity: 0.8 }, children: ["(", actualPoints.length, ")"] }))] }) })] }), _jsx(MapComponent, { width: width, height: height, showCard: showCard, showLoadingSpinner: showLoadingSpinner, showErrorAlert: showErrorAlert, config: {
                    center: {
                        address: "Москва, Россия",
                        latitude: 55.7558,
                        longitude: 37.6176,
                    },
                    zoom: 12,
                } })] }));
});
RouteTracking.displayName = "RouteTracking";
export default RouteTracking;
